import { clsx } from 'clsx';
import { CSSProperties, FocusEvent, forwardRef, Ref } from 'react';
import {
  SearchField as AriaSearchField,
  Text as AriaText,
  SearchFieldProps as AriaSearchFieldProps,
  FieldError,
  Input,
  Label,
} from 'react-aria-components';
import IconButton from '~/components/buttons/NewIconButton';
import fieldStyles from '~/components/inputs/Field.module.scss';
import IconClose from '~/icons/icon-close.svg?react';
import IconSearch from '~/icons/icon-search.svg?react';
import styles from './TextField.module.scss';

interface Props {
  autofocus?: boolean;
  className?: string;
  errorMessage?: string;
  helpText?: string;
  hideLabel?: boolean;
  id?: string;
  name?: string;
  label: string;
  onBlur?: (e: FocusEvent<Element, Element>) => void;
  onFocus?: AriaSearchFieldProps['onFocus'];
  onChange: (value: string) => void;
  onKeyUp?: AriaSearchFieldProps['onKeyUp'];
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  style?: CSSProperties;
  value: string;
}

const SearchField = forwardRef(
  (
    {
      autofocus,
      className,
      errorMessage,
      helpText,
      hideLabel,
      id,
      label,
      name,
      onBlur,
      onChange,
      onFocus,
      size = 'medium',
      style,
      value,
      placeholder,
      onKeyUp,
    }: Props,
    ref: Ref<HTMLDivElement>,
  ) => {
    return (
      <AriaSearchField
        aria-label={hideLabel ? label : undefined}
        autoFocus={autofocus}
        className={clsx(fieldStyles.field, styles.inputField, className)}
        data-size={size}
        id={id}
        isInvalid={!!errorMessage}
        name={name}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        onKeyUp={onKeyUp}
        style={style}
        value={value}
        ref={ref}
      >
        {!hideLabel && <Label>{label}</Label>}
        <div className={styles.inputWrapper}>
          <IconSearch className={styles.icon} />
          <Input placeholder={placeholder} />
          {value.length > 0 && (
            <IconButton
              className={styles.control}
              Icon={IconClose}
              variant="ghost"
              size={size === 'large' ? 'large' : 'small'}
            />
          )}
        </div>
        {helpText && <AriaText slot="description">{helpText}</AriaText>}
        <FieldError>{errorMessage}</FieldError>
      </AriaSearchField>
    );
  },
);
export default SearchField;
